import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Stack,
  Chip,
  alpha,
  useTheme,
  CircularProgress,
  Pagination,
} from "@mui/material";
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { useParams } from "react-router-dom";
import { useQuery } from "react-query";
import { useSnackbar } from "notistack";
import CommunicationNoteDialog from "./Dialog/CommunicationNoteDialog";
import { QK } from "../../../../../base/qk";
import { fetchCommunicationNotes } from "../../../../../api/services/Communication/communicationNote";
import CommunicationFormDialog from "./Dialog/CommunicationFormDialog";
import DataTable from "../../../../Table/DataTable";

const CommunicationTable = () => {
  const theme = useTheme();
  const { clientID } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [formOpen, setFormOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);

  const { data, isLoading, error, refetch, isFetching } = useQuery(
    [QK.commuicationNotes, clientID, page, rowsPerPage],
    () => fetchCommunicationNotes(clientID, page, rowsPerPage),
    { 
      enabled: !!clientID, 
      keepPreviousData: true, 
      retry: false,
      onError: () => {
        enqueueSnackbar("Failed to load communication notes", { variant: "error" });
      }
    }
  );

  const handleView = (id) => {
    const note = data?.results?.find(n => n.id === id);
    if (note) {
      setSelectedNote(note);
      setViewOpen(true);
    }
  };

  const handleCloseView = () => {
    setViewOpen(false);
    setSelectedNote(null);
  };

  const handleRefresh = () => {
    refetch();
    enqueueSnackbar("Refreshed successfully", { variant: "success" });
  };

  const handleChangePage = (event, value) => {
    setPage(value);
  };

  const getInitials = (name) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map(word => word[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const formatDateTime = (datetime) => {
    if (!datetime) return "-";
    try {
      const date = new Date(datetime);
      return date.toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
    } catch {
      return datetime;
    }
  };

  const communicationData = data?.results || [];
  const totalCount = communicationData.length;
  const totalPages = Math.ceil(totalCount / rowsPerPage);

  const headers = [
    { 
      key: "name", 
      label: "Staff Name",
      render: (item) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Typography variant="caption" fontWeight={700} color="primary" fontSize="0.65rem">
              {getInitials(item.name)}
            </Typography>
          </Box>
          <Typography variant="body2" fontWeight={600} color="#1A1A2E">
            {item.name || "-"}
          </Typography>
        </Box>
      )
    },
    { 
      key: "position_name", 
      label: "Position",
      render: (item) => (
        <Chip
          label={item.position_name || "N/A"}
          size="small"
          sx={{
            height: 22,
            fontSize: '0.65rem',
            fontWeight: 600,
            bgcolor: alpha(theme.palette.primary.main, 0.06),
            color: theme.palette.primary.main,
          }}
        />
      )
    },
    { 
      key: "reason", 
      label: "Reason",
      render: (item) => (
        <Typography variant="body2" color="#475467" sx={{ 
          maxWidth: 200,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}>
          {item.reason || "-"}
        </Typography>
      )
    },
    { 
      key: "datetime", 
      label: "Date & Time",
      render: (item) => (
        <Box>
          <Typography variant="body2" fontWeight={500} color="#1A1A2E">
            {formatDateTime(item.datetime)}
          </Typography>
        </Box>
      )
    },
    { 
      key: "sharing", 
      label: "Shared With",
      render: (item) => {
        const shares = [];
        if (item.handover) shares.push({ label: "Handover", color: theme.palette.primary.main });
        if (item.manager) shares.push({ label: "Manager", color: theme.palette.success.main });
        if (item.family) shares.push({ label: "Family", color: theme.palette.warning.main });
        
        if (shares.length === 0) {
          return (
            <Chip 
              label="Private" 
              size="small" 
              sx={{ 
                fontSize: '0.6rem', 
                height: 20,
                bgcolor: '#f1f5f9', 
                color: '#64748b',
                fontWeight: 600
              }} 
            />
          );
        }
        
        return (
          <Stack direction="row" spacing={0.5} flexWrap="wrap">
            {shares.map((share) => (
              <Chip
                key={share.label}
                label={share.label}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.6rem',
                  fontWeight: 600,
                  bgcolor: alpha(share.color, 0.1),
                  color: share.color,
                }}
              />
            ))}
          </Stack>
        );
      }
    },
  ];

  const renderCell = (item, header) => {
    if (header.render) {
      return header.render(item);
    }
    const key = typeof header === 'object' ? header.key : header;
    return item[key] || "-";
  };

  return (
    <Box>
      {isLoading ? (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: 300,
          gap: 2
        }}>
          <CircularProgress size={40} />
          <Typography variant="body2" color="text.secondary">
            Loading communication notes...
          </Typography>
        </Box>
      ) : error ? (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: 300,
          gap: 2
        }}>
          <Typography variant="h6" color="error">
            Failed to load communication notes
          </Typography>
          <Button variant="outlined" onClick={handleRefresh}>
            Retry
          </Button>
        </Box>
      ) : (
        <>
          <DataTable
            data={communicationData}
            headers={headers}
            onView={handleView}
            loading={isLoading}
            renderCell={renderCell}
            actionColumnLabel="Actions"
          />

          {totalPages > 1 && (
            <Box display="flex" justifyContent="center" mt={3}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handleChangePage}
                color="primary"
                shape="rounded"
                sx={{
                  '& .MuiPaginationItem-root': {
                    fontWeight: 600,
                  },
                }}
              />
            </Box>
          )}
        </>
      )}

      <CommunicationFormDialog
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          refetch();
        }}
      />

      <CommunicationNoteDialog
        open={viewOpen}
        onClose={handleCloseView}
        note={selectedNote}
      />
    </Box>
  );
};

export default CommunicationTable;